import { groupBy } from "../util/fp";
import { countWords } from "../util/text";

const MAX_CUSTOMER_TEXT_LENGTH = 1000;

const CATEGORIES = Object.freeze({
  QUOTE: "quote",
  DOODLE: "doodle",
  CUSTOM: "custom",
});

/**
 * Transform a raw API service object into a frontend domain object.
 */
const toDomainService = (raw) => ({
  influencerServiceId: raw.influencerServiceId,
  influencerId: raw.influencerId,
  influencerName: raw.influencerName ?? null,
  category: raw.category,
  label: raw.label,
  description: raw.description ?? "",
  splitAmount: raw.splitAmount,
  maxWordCount: raw.maxWordCount ?? null,
  requiresTextInput: Boolean(raw.requiresTextInput),
  maxQuantity: raw.maxQuantity ?? 1,
  exclusionGroup: raw.exclusionGroup ?? null,
  position: raw.position ?? 0,
  isActive: Boolean(raw.active),
  bumpAmount: raw.bumpAmount ?? 0,
  totalPrice: raw.totalPrice ?? 0,
});

const isQuoteService = (service) => service.category === CATEGORIES.QUOTE;

const isDoodleService = (service) => service.category === CATEGORIES.DOODLE;

const isCustomService = (service) => service.category === CATEGORIES.CUSTOM;

/**
 * Validate customer text against a service's constraints.
 * Returns { valid, error }.
 */
const validateText = (text, service) => {
  if (service.requiresTextInput && (!text || text.trim().length === 0)) {
    return { valid: false, error: "Text is required for this service." };
  }

  if (text && text.length > MAX_CUSTOMER_TEXT_LENGTH) {
    return {
      valid: false,
      error: `Text must be ${MAX_CUSTOMER_TEXT_LENGTH} characters or fewer.`,
    };
  }

  if (text && service.maxWordCount) {
    if (countWords(text) > service.maxWordCount) {
      return {
        valid: false,
        error: `Text must be ${service.maxWordCount} words or fewer.`,
      };
    }
  }

  return { valid: true, error: null };
};

const textOf = (service) => service.customerText;

/**
 * The first customer-text problem across a set of service instances, prefixed
 * with the service it belongs to — or null when every one of them validates.
 *
 * `getText` supplies the value to check, so a caller holding an unsaved draft
 * can validate that instead of what is stored: the cart editor checks what is
 * in the textarea, the product page checks what is on the instance.
 *
 * @param {Array<object>} services
 * @param {function} [getText]
 * @returns {string|null}
 */
const firstTextError = (services, getText = textOf) => {
  const failure = (services ?? [])
    .map((service) => ({
      label: service.label,
      ...validateText(getText(service), service),
    }))
    .find(({ valid }) => !valid);

  return failure ? `${failure.label}: ${failure.error}` : null;
};

/**
 * Group services by influencer ID.
 * Returns a plain object of influencerId → [services].
 */
const groupByInfluencer = groupBy((service) => service.influencerId);

/**
 * How many instances of a service are already selected.
 * Works on any instance shape carrying `influencerServiceId` — product page
 * state and cart_product_service rows both qualify.
 */
const countInstancesOf = (service, instances = []) =>
  instances.filter(
    (instance) => instance.influencerServiceId === service.influencerServiceId,
  ).length;

/**
 * True when the service cannot be added again without exceeding maxQuantity.
 */
const isAtMaxQuantity = (service, instances = []) =>
  countInstancesOf(service, instances) >= (service.maxQuantity ?? 1);

/**
 * True when a different service from the same exclusion group is selected.
 * A null/empty exclusionGroup never conflicts.
 */
const hasExclusionConflict = (service, instances = []) =>
  Boolean(service.exclusionGroup) &&
  instances.some(
    (instance) =>
      instance.exclusionGroup === service.exclusionGroup &&
      instance.influencerServiceId !== service.influencerServiceId,
  );

/**
 * True when the service can still be added to the current selection.
 */
const canAddService = (service, instances = []) =>
  !isAtMaxQuantity(service, instances) &&
  !hasExclusionConflict(service, instances);

export {
  CATEGORIES,
  MAX_CUSTOMER_TEXT_LENGTH,
  canAddService,
  countInstancesOf,
  countWords,
  firstTextError,
  groupByInfluencer,
  hasExclusionConflict,
  isAtMaxQuantity,
  isCustomService,
  isDoodleService,
  isQuoteService,
  toDomainService,
  validateText,
};
